import { TRANSFER } from '../../models/transfer';
import { CUSTOMER_COMPANY_ID, CUSTOMER_PRIVATE_ID, SERVICES } from '../../../constants';
import { SERVICE_TYPE_ID } from '../../models/service-type';

// Removes leading 0 after country code: +49 0171... → +49 171..., +490171... → +49171...
// Two-pass to avoid false positives on NANP (+1) numbers stored without separator (e.g. +1201...).
const normalizePhone = (value) => {
    if (!value) return value;
    return value
        .replace(/^(\+\d{1,3}) 0(\d)/, '$1 $2') // with space: safe for all country codes
        .replace(/^(\+[2-9]\d{1,2})0(\d)/, '$1$2'); // no space: only 2–3 digit CCs starting 2–9
};

/**
 * It removes from booking object data which is for internal use and corrects some properties
 * For now it cleans:
 * - addressFrom
 * - addressTo
 * - serviceType
 * - flight
 * - price
 * - busy
 * - touched
 * Corrects:
 * - adults
 * - children
 * - kids
 * - extras
 * - user customerTypeId
 * - phone numbers (strips leading 0 after country code)
 */
const bookingClean = (booking) => {
    let r = booking;

    for (let i = 0; i <= 1; i++) {
        if (!r.transfers[i]) {
            continue;
        }
        if (r.transfers[i].typeId === TRANSFER.SPECIAL) {
            r = r.setIn(['transfers', i], r.transfers[i].without('flight'));
            r = r.setIn(
                ['transfers', i, 'addressFrom'],
                r.transfers[i].addressFrom.without('id', 'type', 'placeValue'),
            );
            r = r.setIn(['transfers', i, 'addressTo'], r.transfers[i].addressTo.without('id', 'type', 'placeValue'));
            r = r.setIn(['transfers', i, 'extras'], {});
            if (!r.getIn(['transfers', i, 'addressFrom', 'zip'])) {
                r = r.setIn(['transfers', i, 'addressFrom', 'zip'], '-');
            }
            if (!r.getIn(['transfers', i, 'addressTo', 'zip'])) {
                r = r.setIn(['transfers', i, 'addressTo', 'zip'], '-');
            }
        }
        if ([TRANSFER.FROM_MUNICH, TRANSFER.FROM_NUREMBERG].includes(r.transfers[i].typeId)) {
            r = r.setIn(['transfers', i], r.transfers[i].without('addressFrom'));
            r = r.setIn(['transfers', i, 'addressTo'], r.transfers[i].addressTo.without('id', 'type', 'placeValue'));
            if (r.getIn(['transfers', i, 'extras', 'ARRIVE_EARLY'])) {
                r = r.setIn(['transfers', i, 'extras', 'ARRIVE_EARLY'], undefined);
            }
            if (r.getIn(['transfers', i, 'extras', 'ARRIVE_LATE'])) {
                r = r.setIn(['transfers', i, 'extras', 'ARRIVE_LATE'], undefined);
            }
        }
        if ([TRANSFER.TO_MUNICH, TRANSFER.TO_NUREMBERG].includes(r.transfers[i].typeId)) {
            r = r.setIn(['transfers', i], r.transfers[i].without('addressTo'));
            r = r.setIn(
                ['transfers', i, 'addressFrom'],
                r.transfers[i].addressFrom.without('id', 'type', 'placeValue'),
            );
            if (r.getIn(['transfers', i, 'extras', 'GATE'])) {
                r = r.setIn(['transfers', i, 'extras', 'GATE'], undefined);
            }
        }

        r = r.setIn(['transfers', i], r.transfers[i].without('serviceType'));

        // EXTRA_LEGROOM is a COLLECTIVE-only upsell; strip it when the customer
        // ends up on a different tier (e.g. after using the back button).
        if (
            r.transfers[i].serviceTypeId !== SERVICE_TYPE_ID.COLLECTIVE &&
            r.transfers[i].extras[SERVICES.EXTRA_LEGROOM]
        ) {
            r = r.setIn(['transfers', i, 'extras'], r.transfers[i].extras.without(SERVICES.EXTRA_LEGROOM));
        }

        // Per-leg sanity: clamp EXTRA_LEGROOM down to that leg's passenger count. Round-trip
        // legs may have different passenger counts; the Services dropdown is sized to the
        // larger leg, so the lighter leg keeps as many legroom seats as it has passengers
        // rather than losing the extra entirely.
        const legPassengers =
            (r.transfers[i].adults || 0) + (r.transfers[i].children || 0) + (r.transfers[i].kids || 0);
        const storedLegroom = Number(r.transfers[i].extras[SERVICES.EXTRA_LEGROOM] || 0);
        if (storedLegroom > legPassengers) {
            if (legPassengers > 0) {
                r = r.setIn(['transfers', i, 'extras', SERVICES.EXTRA_LEGROOM], legPassengers);
            } else {
                r = r.setIn(['transfers', i, 'extras'], r.transfers[i].extras.without(SERVICES.EXTRA_LEGROOM));
            }
        }

        r = ['adults', 'children', 'kids'].reduce((ri, key) => {
            if (ri.transfers[i][key]) {
                return ri;
            }
            return ri.setIn(['transfers', i, key], 0);
        }, r);

        // count suitcases including free of charge
        if (r.transfers[i].extras.onlyHandLuggage) {
            /*
                is this really needed? seems the object comes without anything else than the
                onlyHandLuggage property anyway (might be it's returned like that from the
                server). anyway, it's not working the way it was written before.
             */
            r = r.setIn(['transfers', i, 'extras'], r.transfers[i].extras.without('ADD'));
        } else {
            // Don't add free allowances here - the backend handles this in price calculation
            // Just ensure ADD is set to the user's input (or 0 if not set)
            const userInputADD = r.transfers[i].extras.ADD || 0;
            r = r.setIn(['transfers', i, 'extras', 'ADD'], userInputADD);
        }

        // convert extras to string
        r = r.setIn(['transfers', i, 'extras'], JSON.stringify(r.transfers[i].extras));
    }

    r = r.setIn(
        ['user', 'customerTypeId'],
        r.getIn(['user', 'companyName']) ? CUSTOMER_COMPANY_ID : CUSTOMER_PRIVATE_ID,
    );

    if (r.company.id) {
        r = r.setIn(['companyId'], r.getIn(['company', 'id']));

        if (r.company.costCenter) {
            r = r.setIn(['customerCostCenter'], r.getIn(['company', 'costCenter']));
        }

        if (r.company.orderNumber) {
            r = r.setIn(['orderNumber'], r.getIn(['company', 'orderNumber']));
        }
    }

    r = r.without('price', 'busy', 'touched', 'company');

    r = r.setIn(['user', 'mobile'], normalizePhone(r.getIn(['user', 'mobile'])));
    r = r.setIn(['user', 'address', 'phone'], normalizePhone(r.getIn(['user', 'address', 'phone'])));
    if (r.passengerPhone) {
        r = r.setIn(['passengerPhone'], normalizePhone(r.passengerPhone));
    }

    return r;
};

export { bookingClean };
